import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Check, Eye, GitBranch, Loader2, Trash2 } from 'lucide-react';
import { marked } from 'marked';
import { toast } from 'sonner';
import {
  CTO_FOH_TOOLS,
  CTO_PROMPT_STATUSES,
  createPrompt,
  deletePrompt,
  fetchCtoPrompts,
  findPrompt,
  isValidSlug,
  nextPosition,
  updatePrompt,
  type CtoPromptRow,
  type CtoPromptStatus,
} from '../../lib/ctoPrompts';

/**
 * One prompt, full width.
 *
 * The body is the artefact — everything else on this page is metadata about when it runs.
 * It gets a page rather than a modal because these are two thousand words each and they are
 * written here, not pasted in finished.
 */

interface Draft {
  title: string;
  slug: string;
  triggerMd: string;
  bodyMd: string;
  fohTool: string;
  whenMd: string;
  whyMd: string;
  position: number;
  status: CtoPromptStatus;
}

function draftFrom(p: CtoPromptRow): Draft {
  return {
    title: p.title,
    slug: p.slug,
    triggerMd: p.triggerMd ?? '',
    bodyMd: p.bodyMd,
    fohTool: p.fohTool ?? '',
    whenMd: p.whenMd ?? '',
    whyMd: p.whyMd ?? '',
    position: p.position,
    status: p.status,
  };
}

/** Long enough that a pause in a sentence is not a write, short enough to feel automatic. */
const AUTOSAVE_DELAY_MS = 1200;

/**
 * Why a draft can be refused rather than saved. Autosave means nobody presses a button, so
 * the reason has to be visible where the button used to be.
 */
function invalidReason(draft: Draft): string | null {
  if (draft.title.trim().length < 3) return 'Title needs at least 3 characters';
  if (!isValidSlug(draft.slug)) return 'Slug must be lowercase words joined by hyphens';
  return null;
}

/**
 * Stands where the save button used to. With no button to press, this is the only thing
 * telling you the work is safe — so it says why it is not, rather than only that it is not.
 */
function SaveState({
  saving,
  dirty,
  problem,
  error,
  savedAt,
}: {
  saving: boolean;
  dirty: boolean;
  problem: string | null;
  error: string | null;
  savedAt: Date | null;
}) {
  if (error) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
        <AlertTriangle className="h-4 w-4" />
        Not saved — {error}
      </span>
    );
  }
  if (problem && dirty) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-amber-600 dark:text-amber-400">
        <AlertTriangle className="h-4 w-4" />
        Not saved — {problem}
      </span>
    );
  }
  if (saving) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        Saving…
      </span>
    );
  }
  if (dirty) {
    return <span className="text-sm text-gray-400">Unsaved changes…</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400">
      <Check className="h-4 w-4" />
      {savedAt
        ? `Saved at ${savedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
        : 'Saved'}
    </span>
  );
}

const LABEL = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';
const FIELD =
  'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white';

export default function CtoPromptDetailView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [prompt, setPrompt] = useState<CtoPromptRow | null>(null);
  const [parent, setParent] = useState<CtoPromptRow | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const autosaveTimer = useRef<number | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      // The whole tree, so the parent's title is available without a second round trip.
      const roots = await fetchCtoPrompts();
      const found = findPrompt(roots, id);
      setPrompt(found);
      setParent(found?.parentId ? findPrompt(roots, found.parentId) : null);
      setDraft(found ? draftFrom(found) : null);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load the prompt');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(() => {
    if (!prompt || !draft) return false;
    const original = draftFrom(prompt);
    return (Object.keys(original) as (keyof Draft)[]).some((k) => original[k] !== draft[k]);
  }, [prompt, draft]);

  const problem = draft ? invalidReason(draft) : null;

  /**
   * Saves and updates the baseline in place. Refetching instead would replace `draft` with
   * whatever came back mid-sentence, and the letters typed during the round trip would
   * vanish under the cursor.
   */
  const save = useCallback(async (current: Draft, target: CtoPromptRow) => {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updatePrompt(target.id, {
        title: current.title.trim(),
        slug: current.slug,
        triggerMd: current.triggerMd.trim() || null,
        bodyMd: current.bodyMd,
        fohTool: current.fohTool.trim() || null,
        whenMd: current.whenMd.trim() || null,
        whyMd: current.whyMd.trim() || null,
        position: current.position,
        status: current.status,
      });
      // The update returns one row; children come from the tree read and are still correct.
      setPrompt((prev) => (prev ? { ...updated, children: prev.children } : updated));
      setSavedAt(new Date());
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  }, []);

  useEffect(() => {
    if (!prompt || !draft || !dirty || saving || problem) return;
    if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    autosaveTimer.current = window.setTimeout(() => {
      void save(draft, prompt);
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    };
  }, [prompt, draft, dirty, saving, problem, save]);

  // Closing the tab inside the debounce window would drop the last edit silently.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  /** Navigation cannot outrun the debounce, or the last sentence typed is lost. */
  const leaveTo = async (to: string) => {
    if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    if (prompt && draft && dirty && !problem) await save(draft, prompt);
    navigate(to);
  };

  const addChild = async () => {
    if (!prompt) return;
    if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    if (draft && dirty && !problem) await save(draft, prompt);
    setSaving(true);
    try {
      const created = await createPrompt({
        parentId: prompt.id,
        slug: `deeper-pass-${Date.now().toString(36).slice(-5)}`,
        title: 'New deeper pass',
        role: prompt.role,
        triggerMd: 'Runs when …',
        position: nextPosition(prompt.children),
      });
      navigate(`/cto/frameworks/${created.id}`);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not add the deeper pass');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!prompt) return;
    const kids = prompt.children.length;
    const extra = kids ? ` and its ${kids} deeper pass${kids === 1 ? '' : 'es'}` : '';
    if (!window.confirm(`Delete “${prompt.title}”${extra}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await deletePrompt(prompt.id);
      toast.success('Deleted');
      navigate('/cto/frameworks');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not delete');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 py-24">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading…
      </div>
    );
  }

  if (!prompt || !draft) {
    return (
      <div className="max-w-5xl mx-auto py-24 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">That prompt no longer exists.</p>
        <button
          type="button"
          onClick={() => navigate('/cto/frameworks')}
          className="text-sm text-fuchsia-600 dark:text-fuchsia-400 hover:underline"
        >
          Back to frameworks
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <button
        type="button"
        onClick={() => void leaveTo('/cto/frameworks')}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Frameworks
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{prompt.title}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {parent ? `A deeper pass under ${parent.title}.` : 'A top-level prompt.'}{' '}
          {draft.triggerMd.trim()
            ? 'It runs only when its trigger is met.'
            : 'It runs on every review.'}
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className={LABEL}>Title</span>
            <input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className={FIELD}
            />
          </label>
          <label className="block">
            <span className={LABEL}>Slug</span>
            <input
              value={draft.slug}
              onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
              className={`${FIELD} font-mono ${isValidSlug(draft.slug) ? '' : 'border-red-400'}`}
            />
          </label>
        </div>

        <label className="block">
          <span className={LABEL}>Runs when</span>
          <input
            value={draft.triggerMd}
            onChange={(e) => setDraft({ ...draft, triggerMd: e.target.value })}
            placeholder="Leave empty to run on every review"
            className={FIELD}
          />
          <span className="block text-xs text-gray-400 mt-1">
            When this review pass itself runs. Plain words — the model doing the detecting reads this.
          </span>
        </label>

        <div className="rounded-lg border border-fuchsia-200/70 dark:border-fuchsia-900/50 bg-fuchsia-50/40 dark:bg-fuchsia-950/20 p-4 space-y-3">
          <p className="text-xs font-medium text-fuchsia-800 dark:text-fuchsia-300">
            Advisor routing
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
            What the front-of-house MCP should do after someone asks a related question.
          </p>
          <label className="block">
            <span className={LABEL}>Front-of-house tool</span>
            <select
              value={draft.fohTool}
              onChange={(e) => setDraft({ ...draft, fohTool: e.target.value })}
              className={`${FIELD} font-mono`}
            >
              <option value="">None yet</option>
              {CTO_FOH_TOOLS.map((tool) => (
                <option key={tool} value={tool}>
                  {tool}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={LABEL}>Use when</span>
            <textarea
              value={draft.whenMd}
              onChange={(e) => setDraft({ ...draft, whenMd: e.target.value })}
              rows={2}
              placeholder="The subscriber wants to know if the app can be seen in production…"
              className={FIELD}
            />
          </label>
          <label className="block">
            <span className={LABEL}>Why this tool</span>
            <textarea
              value={draft.whyMd}
              onChange={(e) => setDraft({ ...draft, whyMd: e.target.value })}
              rows={2}
              placeholder="A scored observability pass is the next useful action, not more reading."
              className={FIELD}
            />
          </label>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <label className="block">
            <span className={LABEL}>Status</span>
            <select
              value={draft.status}
              onChange={(e) => setDraft({ ...draft, status: e.target.value as CtoPromptStatus })}
              className={`${FIELD} w-auto`}
            >
              {CTO_PROMPT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={LABEL}>Order</span>
            <input
              type="number"
              value={draft.position}
              onChange={(e) => setDraft({ ...draft, position: Number(e.target.value) || 0 })}
              className={`${FIELD} w-24`}
            />
          </label>
          <button
            type="button"
            onClick={() => void addChild()}
            disabled={saving}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <GitBranch className="h-4 w-4" />
            Add deeper pass
          </button>
        </div>

        {prompt.children.length > 0 && (
          <div>
            <span className={LABEL}>Deeper passes</span>
            <div className="flex flex-wrap gap-2">
              {prompt.children.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => void leaveTo(`/cto/frameworks/${c.id}`)}
                  className="px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {c.title}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Prompt</span>
            <button
              type="button"
              onClick={() => setPreview((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              <Eye className="h-3.5 w-3.5" />
              {preview ? 'Edit' : 'Preview'}
            </button>
          </div>
          {preview ? (
            <div
              className="prose prose-sm dark:prose-invert max-w-none min-h-[32rem] px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-auto"
              dangerouslySetInnerHTML={{ __html: String(marked.parse(draft.bodyMd)) }}
            />
          ) : (
            <textarea
              value={draft.bodyMd}
              onChange={(e) => setDraft({ ...draft, bodyMd: e.target.value })}
              spellCheck
              className="w-full min-h-[32rem] px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm font-mono leading-relaxed text-gray-900 dark:text-white"
            />
          )}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
          <button
            type="button"
            onClick={() => void remove()}
            disabled={saving}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
          <SaveState
            saving={saving}
            dirty={dirty}
            problem={problem}
            error={saveError}
            savedAt={savedAt}
          />
        </div>
      </div>
    </div>
  );
}
